import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import LoginScreen from './components/LoginScreen';
import WelcomeScreen from './components/WelcomeScreen';
import GameOverScreen from './components/GameOverScreen';
import GameComponent from './components/GameComponent';
import CategoryCompletionScreen from './components/CategoryCompletionScreen';
import Menu from './components/Menu';
import LimitedMenu from './components/LimitedMenu';
import Leaderboard from './components/Leaderboard';
import Profile from './components/Profile';
import StudyDetails from './components/StudyDetails';
import StudyDetailsExternal from './components/StudyDetailsExternal';
import PrivacyNotice from './components/PrivacyNotice';
import ConsentModal from './components/ConsentModal';
import { hasToken, subscribeToAuth } from './api';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { userApi } from './api';

const ProtectedRoute: React.FC<{ children: React.ReactNode; useJWT: boolean }> = ({ children, useJWT }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [showConsent, setShowConsent] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isDevelopment = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';
  const jwtAuthenticated = useSyncExternalStore(subscribeToAuth, hasToken);

  // Check if current page should show consent modal
  const shouldShowConsent = () => {
    const consentExcludedPaths = ['/privacy-notice', '/study-details', '/study-details-external'];
    return !consentExcludedPaths.includes(location.pathname);
  };

  useEffect(() => {
    if (!useJWT) {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        setIsAuthenticated(!!user);
        if (!user && (location.pathname !== '/login' && location.pathname !== '/study-details-external')) {
          navigate('/login');
        } else if (user) {
          try {
            const response = await userApi.getProfile();
            if (response.data.status === 'success' && response.data.exists) {
              const userData = response.data.user;
              if (!userData.answered_consent_popup && shouldShowConsent()) {
                setShowConsent(true);
              }
            }
          } catch (error) {
            console.error('Error checking consent status:', error);
          }
        }
      });

      return () => unsubscribe();
    }
  }, [location.pathname, navigate, useJWT]);

  const consentExcludedPaths = ['/privacy-notice', '/study-details', '/study-details-external'];
  useEffect(() => {
    if (consentExcludedPaths.includes(location.pathname)) {
      setShowConsent(false);
    }
  }, [location.pathname]);

  if (useJWT) {
    if (!isDevelopment && jwtAuthenticated === null) {
      return <div>Loading...</div>;
    }

    if (!isDevelopment && !jwtAuthenticated) {
      return <div>Waiting for authentication...</div>;
    }
  } else {
    if (isAuthenticated === null) {
      return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
      return null;
    }
  }

  return (
    <>
      {children}
      {showConsent && shouldShowConsent() && <ConsentModal onClose={() => setShowConsent(false)} />}
    </>
  );
};

function AppRoutes({ useJWT }: { useJWT: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!useJWT) {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          try {
            const response = await userApi.getProfile();
            if (response.data.status === 'success') {
              if (response.data.exists) {
                if (location.pathname === '/login') {
                  navigate('/welcome');
                }
              } else {
                if (location.pathname !== '/welcome' && location.pathname !== '/study-details' && location.pathname !== '/privacy-notice' && location.pathname !== '/study-details-external') {
                  navigate('/welcome');
                }
              }
            } else {
              console.error('Failed to check user profile');
              navigate('/login');
            }
          } catch (err) {
            console.error('Error checking user profile:', err);
            navigate('/login');
          }
        } else {
          if (location.pathname !== '/login' && location.pathname !== '/study-details-external') {
            navigate('/login');
          }
        }
      });

      return () => unsubscribe();
    }
  }, [navigate, location.pathname, useJWT]);

  return (
    <Routes>
      {!useJWT && (
        <>
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/study-details-external" element={
            <>
              <LimitedMenu />
              <StudyDetailsExternal />
            </>
          } />
        </>
      )}
      <Route path="/welcome" element={
        <ProtectedRoute useJWT={useJWT}>
          <>
            <Menu />
            <WelcomeScreen />
          </>
        </ProtectedRoute>
      } />
      <Route path="/play" element={
        <ProtectedRoute useJWT={useJWT}>
          <>
            <Menu />
            <GameComponent />
          </>
        </ProtectedRoute>
      } />
      <Route path="/category-completion" element={
        <ProtectedRoute useJWT={useJWT}>
          <>
            <Menu />
            <CategoryCompletionScreen categoryName={location.state?.categoryName || 'current'} />
          </>
        </ProtectedRoute>
      } />
      <Route path="/results" element={
        <ProtectedRoute useJWT={useJWT}>
          <>
            <Menu />
            <GameOverScreen />
          </>
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute useJWT={useJWT}>
          <>
            <Menu />
            <Leaderboard />
          </>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute useJWT={useJWT}>
          <>
            <Menu />
            <Profile />
          </>
        </ProtectedRoute>
      } />
      <Route path="/study-details" element={
        <ProtectedRoute useJWT={useJWT}>
          <>
            <Menu />
            <StudyDetails />
          </>
        </ProtectedRoute>
      } />
      <Route path="/privacy-notice" element={
        <ProtectedRoute useJWT={useJWT}>
          <>
            <Menu />
            <PrivacyNotice />
          </>
        </ProtectedRoute>
      } />
      {useJWT && (
        <>
          <Route path="/" element={<Navigate to="/welcome" replace />} />
        </>
      )}
      {!useJWT && (
        <>
          <Route path="/" element={<Navigate to="/login" replace />} />
        </>
      )}
    </Routes>
  );
}

function App() {
  const [shouldUseJWTAuthentication, setShouldUseJWTAuthentication] = useState(false);

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const safari = /safari/.test(userAgent);
    const ios = /iphone|ipod|ipad/.test(userAgent);
    console.log(userAgent);
    
    if (ios) {
      if (safari) {
        setShouldUseJWTAuthentication(false);
      } else if (!safari) {
        setShouldUseJWTAuthentication(true);
      }
    } else {
      const isInIframe = window.self !== window.top;
      setShouldUseJWTAuthentication(isInIframe);
    }
  }, []);

  return (
    <Router>
      <AppRoutes useJWT={shouldUseJWTAuthentication} />
    </Router>
  );
}

export default App;